import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button, Field, Input, Chip, Spinner } from "@/components/kit";
import { api, formatApiError } from "@/lib/api";

const AGES = Array.from({ length: 82 }, (_, i) => i + 18); // 18..99

export default function PreferencesStep({ reference, onNext, onBack }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/preferences").then(({ data }) => setForm({
      age_min: data.age_min || 25,
      age_max: data.age_max || 45,
      preferred_genders: data.preferred_genders || [],
      relationship_intents: data.relationship_intents || [],
      cities: data.cities || [],
      cityText: (data.cities || []).join(", "),
    }));
  }, []);

  if (!form) return <div className="flex justify-center py-10"><Spinner /></div>;

  const toggle = (key, val) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));

  const save = async () => {
    if (!form.preferred_genders.length) { toast.error("Select at least one gender preference."); return; }
    if (!form.relationship_intents.length) { toast.error("Select at least one relationship intent."); return; }
    if (form.age_min > form.age_max) { toast.error("Minimum age cannot exceed maximum age."); return; }
    setSaving(true);
    try {
      await api.put("/preferences", {
        age_min: Number(form.age_min),
        age_max: Number(form.age_max),
        preferred_genders: form.preferred_genders,
        relationship_intents: form.relationship_intents,
        cities: form.cityText.split(",").map((c) => c.trim()).filter(Boolean),
      });
      onNext();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-rise">
      <p className="font-sans text-sm text-[#5F6D66] leading-relaxed">
        These preferences shape who appears in your Discover feed. We never use precise location — only the cities you choose.
      </p>

      <Field label="Age range">
        <div className="flex items-center gap-3">
          <select value={form.age_min} onChange={(e) => setForm((f) => ({ ...f, age_min: e.target.value }))} className="flex-1 rounded-xl border border-[#E3DCD0] bg-white px-4 py-3 text-[15px]" data-testid="pref-age-min">
            {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <span className="text-[#8A9790]">to</span>
          <select value={form.age_max} onChange={(e) => setForm((f) => ({ ...f, age_max: e.target.value }))} className="flex-1 rounded-xl border border-[#E3DCD0] bg-white px-4 py-3 text-[15px]" data-testid="pref-age-max">
            {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </Field>

      <Field label="Genders you'd like to see">
        <div className="flex flex-wrap gap-2">
          {reference.gender_identities.map((g) => (
            <Chip key={g} active={form.preferred_genders.includes(g)} onClick={() => toggle("preferred_genders", g)} data-testid={`pref-gender-${g.replace(/\s+/g, "-").toLowerCase()}`}>{g}</Chip>
          ))}
        </div>
      </Field>

      <Field label="Relationship intentions">
        <div className="flex flex-wrap gap-2">
          {reference.relationship_intents.map((r) => (
            <Chip key={r} active={form.relationship_intents.includes(r)} onClick={() => toggle("relationship_intents", r)} data-testid={`pref-intent-${r.replace(/\s+/g, "-").toLowerCase()}`}>{r}</Chip>
          ))}
        </div>
      </Field>

      <Field label="Preferred cities" hint="Optional. Separate multiple cities with commas.">
        <Input value={form.cityText} onChange={(e) => setForm((f) => ({ ...f, cityText: e.target.value }))} placeholder="e.g. Lisbon, Porto" data-testid="pref-cities-input" />
      </Field>

      <div className="flex gap-3 pt-2">
        <Button variant="outline" className="flex-1" onClick={onBack} data-testid="pref-back-btn">Back</Button>
        <Button className="flex-1" size="lg" onClick={save} disabled={saving} data-testid="pref-continue-btn">
          {saving ? "Saving…" : "Continue"}
        </Button>
      </div>
    </div>
  );
}
